import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  StyleSheet,
  StatusBar,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const DISGO_LOGO_BLACK = require("../../../assets/DisgoLogoBlack.png");
const DISGO_LOGO_BROWN = require("../../../assets/DisgoBrownLogo.png");

/**
 * Polaroid preview + visibility controls for the Be Real check-in flow.
 */
export default function BeRealCheckInScreen({
  restaurant,
  frontPhotoUri,
  backPhotoUri,
  selectedVisibility,
  isSubmitting,
  onBack,
  onSelectVisibility,
  /** Narrow card + bounded width for desktop web modals */
  desktopWebLayout = false,
}) {
  const insets = useSafeAreaInsets();
  const { width: winW, height: winH } = Dimensions.get("window");
  const stageHMargin = 10;
  const effectiveWinW = desktopWebLayout ? Math.min(winW, 520) : winW;
  const cardWidth = Math.max(
    desktopWebLayout ? 280 : 0,
    effectiveWinW - stageHMargin * 2 - insets.left - insets.right
  );
  const cardInnerPaddingH = 20;
  const photoFrameWidth = Math.max(0, cardWidth - cardInnerPaddingH);
  const idealPhotoHeight = photoFrameWidth * (4 / 3);
  const maxPhotoHeight = desktopWebLayout ? winH * 0.42 : winH * 0.58;
  /** RN Web: avoid 0-height frame when layout hasn't measured yet */
  const photoHeight = Math.max(
    desktopWebLayout ? 180 : 0,
    Math.min(idealPhotoHeight, maxPhotoHeight)
  );

  return (
    <>
      <StatusBar barStyle="dark-content" />
      <View style={[styles.polaroidRoot, desktopWebLayout && styles.polaroidRootDesktopWeb]}>
        <View
          style={[
            styles.polaroidHeader,
            {
              paddingTop: Math.max(insets.top, 10),
              paddingLeft: 16 + insets.left,
              paddingRight: 16 + insets.right,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.polaroidCloseBtn}
            onPress={onBack}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Ionicons name="arrow-back" size={24} color="#522504" />
          </TouchableOpacity>
          <Text style={styles.polaroidTitle} numberOfLines={1}>
            Share the Moment!
          </Text>
          <View style={{ width: 40 }} />
        </View>

        <View
          style={[
            styles.polaroidStage,
            desktopWebLayout && styles.polaroidStageDesktopWeb,
            {
              paddingLeft: stageHMargin + insets.left,
              paddingRight: stageHMargin + insets.right,
            },
          ]}
        >
          <View style={[styles.polaroidCard, { width: cardWidth }]}>
            <View style={[styles.polaroidPhotoFrame, { height: photoHeight }]}>
              {backPhotoUri ? (
                <Image source={{ uri: backPhotoUri }} style={styles.polaroidBackImage} resizeMode="cover" />
              ) : (
                <View style={styles.polaroidBackPlaceholder}>
                  <Image source={DISGO_LOGO_BLACK} style={styles.polaroidLogoMain} resizeMode="contain" />
                </View>
              )}
              {(frontPhotoUri || backPhotoUri) && (
                <View style={styles.polaroidFrontWrap}>
                  {frontPhotoUri ? (
                    <Image source={{ uri: frontPhotoUri }} style={styles.polaroidFrontImage} resizeMode="cover" />
                  ) : (
                    <Image source={DISGO_LOGO_BROWN} style={styles.polaroidLogoInset} resizeMode="stretch" />
                  )}
                </View>
              )}
            </View>
            <View style={styles.polaroidBottom}>
              <Text style={styles.polaroidRestaurant} numberOfLines={1}>
                {restaurant?.name || "Restaurant"}
              </Text>
              <Text style={styles.polaroidBrand}>D I S G O</Text>
            </View>
          </View>
        </View>

        <View
          style={[
            styles.polaroidReminderWrap,
            {
              paddingLeft: 16 + insets.left,
              paddingRight: 16 + insets.right,
            },
          ]}
        >
          <Text style={styles.polaroidReminder}>
          {"Don't forget to recap this bite after the moment - enjoy!"}
          </Text>
        </View>

        <View
          style={[
            styles.polaroidActions,
            {
              paddingLeft: 16 + insets.left,
              paddingRight: 16 + insets.right,
              paddingBottom: Math.max(insets.bottom, 16),
            },
          ]}
        >
          <View style={styles.polaroidVisibilityRow}>
            <TouchableOpacity
              style={[
                styles.polaroidPill,
                styles.polaroidPillFriends,
                selectedVisibility === "friends" && styles.polaroidPillSelected,
              ]}
              onPress={() => onSelectVisibility("friends")}
              activeOpacity={0.85}
              disabled={isSubmitting}
            >
              {isSubmitting && selectedVisibility === "friends" ? (
                <ActivityIndicator color="#522504" />
              ) : (
                <Text style={styles.polaroidPillTextFriends}>Friends</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.polaroidPill,
                styles.polaroidPillPublic,
                selectedVisibility === "public" && styles.polaroidPillSelectedPublic,
              ]}
              onPress={() => onSelectVisibility("public")}
              activeOpacity={0.85}
              disabled={isSubmitting}
            >
              {isSubmitting && selectedVisibility === "public" ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.polaroidPillTextPublic}>Public</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.polaroidSquircle,
                selectedVisibility === "only_me" && styles.polaroidSquircleSelected,
              ]}
              onPress={() => onSelectVisibility("only_me")}
              activeOpacity={0.85}
              accessibilityLabel="Only me"
              accessibilityHint="Only you can see this moment on your profile. No one else will see it."
              accessibilityRole="button"
              disabled={isSubmitting}
            >
              {isSubmitting && selectedVisibility === "only_me" ? (
                <ActivityIndicator color="#522504" />
              ) : (
                <>
                  <Ionicons name="lock-closed" size={16} color="#522504" />
                  <Text style={styles.polaroidSquircleText}>Only me</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  polaroidRoot: {
    flex: 1,
    backgroundColor: "#F5F1ED",
  },
  polaroidRootDesktopWeb: {
    flex: 1,
    width: "100%",
    maxWidth: 520,
    minHeight: 520,
    alignSelf: "center",
    borderRadius: 16,
    overflow: "hidden",
  },
  polaroidStageDesktopWeb: {
    flex: 1,
    minHeight: 260,
    justifyContent: "center",
  },
  polaroidHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 10,
  },
  polaroidCloseBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(212,196,184,0.35)",
  },
  polaroidTitle: {
    flex: 1,
    marginHorizontal: 8,
    color: "#522504",
    fontSize: 18,
    fontWeight: "700",
    textAlign: "center",
  },
  polaroidStage: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  polaroidCard: {
    backgroundColor: "#fff",
    borderWidth: 3,
    borderColor: "#D4C4B8",
    padding: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.2,
    shadowRadius: 14,
    elevation: 14,
  },
  polaroidPhotoFrame: {
    width: "100%",
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: "#DCDCDC",
  },
  polaroidBackImage: {
    width: "100%",
    height: "100%",
  },
  polaroidBackPlaceholder: {
    width: "100%",
    height: "100%",
    backgroundColor: "#CCC",
  },
  polaroidLogoMain: {
    width: "100%",
    height: "100%",
  },
  polaroidLogoInset: {
    width: "100%",
    height: "100%",
  },
  polaroidFrontWrap: {
    position: "absolute",
    left: 10,
    top: 10,
    width: "28%",
    aspectRatio: 3 / 4,
    borderRadius: 8,
    overflow: "hidden",
    borderWidth: 2,
    borderColor: "#fff",
    backgroundColor: "#D0D0D0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 4,
    elevation: 5,
  },
  polaroidFrontImage: {
    width: "100%",
    height: "100%",
  },
  polaroidBottom: {
    minHeight: 72,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 4,
    paddingBottom: 6,
  },
  polaroidRestaurant: {
    fontSize: 14,
    fontWeight: "600",
    color: "#6B4C3B",
    marginBottom: 4,
    textAlign: "center",
    paddingHorizontal: 8,
  },
  polaroidBrand: {
    fontSize: 22,
    fontWeight: "700",
    color: "#522504",
    letterSpacing: 4,
  },
  polaroidReminderWrap: {
    paddingTop: 4,
    paddingBottom: 12,
  },
  polaroidReminder: {
    textAlign: "center",
    fontSize: 15,
    fontWeight: "500",
    color: "#7a6558",
    lineHeight: 22,
  },
  polaroidActions: {
    paddingTop: 0,
  },
  polaroidVisibilityRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  polaroidPill: {
    flex: 1,
    minWidth: 0,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 18,
    paddingHorizontal: 12,
    minHeight: 64,
    borderRadius: 32,
  },
  polaroidPillFriends: {
    backgroundColor: "#E6DDD6",
  },
  polaroidPillPublic: {
    backgroundColor: "#522504",
  },
  polaroidPillSelected: {
    borderWidth: 3,
    borderColor: "#522504",
  },
  polaroidPillSelectedPublic: {
    borderWidth: 3,
    borderColor: "#FFD700",
  },
  polaroidPillTextFriends: {
    fontSize: 16,
    fontWeight: "700",
    color: "#522504",
    textAlign: "center",
  },
  polaroidPillTextPublic: {
    fontSize: 16,
    fontWeight: "800",
    color: "#FFFFFF",
    textAlign: "center",
  },
  polaroidSquircle: {
    width: 72,
    height: 72,
    flexShrink: 0,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 4,
    borderRadius: 36,
    borderWidth: 1.5,
    borderColor: "#D4C4B8",
    backgroundColor: "#FFFFFF",
    gap: 3,
  },
  polaroidSquircleSelected: {
    borderWidth: 3,
    borderColor: "#522504",
  },
  polaroidSquircleText: {
    fontSize: 10,
    fontWeight: "700",
    color: "#522504",
    textAlign: "center",
    lineHeight: 12,
  },
});
